import { PhysicalAddress } from '@bufteam/cfacorp_core.bufbuild_es/cfa/core/v1/physical_address_pb';
import { AutoCompleteResult } from '@bufteam/cfacorp_d2c.bufbuild_es/cfa/d2c/frontend/restaurant/v1/restaurant_pb';
import * as z from 'zod';

import {
  AddressResult,
  CateringDeliveryLocation,
  OrderDeliveryAddress,
  ValidatedDeliveryAddress,
} from './schemas';

export const autoCompleteToAddressResult = z.transform<AutoCompleteResult, AddressResult>((i) => ({
  placeId: i.placeId,
  structuredFormatting: {
    mainText: i.structuredFormat?.mainText ?? '',
    secondaryText: i.structuredFormat?.secondaryText ?? '',
  },
}));

export const addressResultToAutoComplete = z.transform<AddressResult, AutoCompleteResult>((i) => ({
  $typeName: 'cfa.d2c.frontend.restaurant.v1.AutoCompleteResult',
  placeId: i.placeId,
  structuredFormat: {
    $typeName: 'cfa.d2c.frontend.restaurant.v1.StructuredFormat',
    mainText: i.structuredFormatting.mainText,
    secondaryText: i.structuredFormatting.secondaryText,
  },
}));

export const physicalToValidatedDeliveryAddress = z.transform<PhysicalAddress, ValidatedDeliveryAddress>(
  (physical) => {
    // Split streetAddress into streetNumber and route
    const trimmedAddress = (physical.streetAddress || '').trim();

    // Handle empty address
    if (!trimmedAddress) {
      return {
        streetNumber: undefined,
        route: undefined,
        subpremise: physical.subPremise ?? undefined,
        locality: physical.locality ?? undefined,
        administrativeAreaLevelOne: physical.administrativeAreaLevelOne ?? undefined,
        administrativeAreaLevelTwo: physical.administrativeAreaLevelTwo ?? undefined,
        postalCode: physical.postalCode ?? undefined,
        country: physical.country ?? undefined,
      };
    }

    const streetParts = trimmedAddress.split(' ');
    const streetNumber = streetParts[0];
    // For route: join remaining parts and trim. Returns empty string if only one word.
    const route = streetParts.length > 1 ? streetParts.slice(1).join(' ').trim() : '';

    return {
      streetNumber,
      route,
      subpremise: physical.subPremise ?? undefined, // Note: case difference - subPremise -> subpremise
      locality: physical.locality ?? undefined,
      administrativeAreaLevelOne: physical.administrativeAreaLevelOne ?? undefined,
      administrativeAreaLevelTwo: physical.administrativeAreaLevelTwo ?? undefined,
      postalCode: physical.postalCode ?? undefined,
      country: physical.country ?? undefined,
    };
  },
);

export const validatedToOrderDeliveryAddress = z.transform<ValidatedDeliveryAddress, OrderDeliveryAddress>(
  (address) => ({
    addressLine1: Boolean(address.streetNumber && address.route)
      ? `${address.streetNumber} ${address.route}`
      : undefined,
    addressLine2: address.subpremise,
    city: address.locality,
    county: address.administrativeAreaLevelTwo,
    state: address.administrativeAreaLevelOne,
    zip: address.postalCode,
  }),
);

export const locationToCateringDeliveryLocation = (loc: {
  locationNumber: string;
  name: string;
  locationContact?: {
    daytimePhone?: { areaCode?: string; phoneNumber?: string; base?: string; extensionNumber?: string };
  };
  mobileOrderingSettings?: {
    fulfillmentMethods?: { cateringPickup?: unknown; cateringDelivery?: unknown };
  };
}): CateringDeliveryLocation => ({
  locationNumber: loc.locationNumber,
  name: loc.name,
  locationContact: { daytimePhone: loc.locationContact?.daytimePhone },
  mobileOrderingSettings: {
    fulfillmentMethods: {
      cateringPickup: Boolean(loc.mobileOrderingSettings?.fulfillmentMethods?.cateringPickup),
      cateringDelivery: Boolean(loc.mobileOrderingSettings?.fulfillmentMethods?.cateringDelivery),
    },
  },
});
